import {
  type CreateOp,
  forgetOp,
  isSending,
  localOps,
  onLocalOps,
  refusedText,
  resendOp,
  useServerOps,
} from '@/api/createOps';
import type { ApiUser } from '@/api/data';
import { invalidateShifts } from '@/api/shifts';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Button, toast } from '@/ui';
import { fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useSyncExternalStore } from 'react';
/**
 * New shifts whose save got no answer (issue #157): kept in this browser, and recorded on the
 * server without a shift, so a reload or another device finds them here rather than the manager
 * making the shift again. «Sjekk» sends the same operation again: the server answers with the
 * shift it made, or makes it once. Nothing is sent without the manager pressing it.
 * The server lists only operations with no shift yet: one that made its shift but whose answer was
 * lost shows here only in the browser that sent it; elsewhere the shift is simply in the plan.
 * A failed server read is said, never shown as an empty list (PR #158 review).
 */
import { refused } from './lock';

export function PendingCreates({ tenant, users }: { tenant: number; users: ApiUser[] }) {
  const qc = useQueryClient();
  const server = useServerOps(true);
  // the browser's list, as a string so the snapshot is stable between changes
  const local = useSyncExternalStore(onLocalOps, () => JSON.stringify(localOps(tenant)));
  const [busy, setBusy] = useState<string | null>(null);
  const ops = new Map<string, CreateOp>();
  for (const o of JSON.parse(local) as CreateOp[]) ops.set(o.op_id, o);
  for (const o of server.data?.ops ?? [])
    if (!ops.has(o.op_id) && !isSending(o.op_id))
      ops.set(o.op_id, { op_id: o.op_id, body: o.body, at: o.created_at });
  if (!ops.size && !server.isError) return null;

  const again = async (o: CreateOp) => {
    if (busy) return;
    setBusy(o.op_id);
    try {
      await resendOp({ ...o.body, op_id: o.op_id });
      forgetOp(o.op_id);
      toast(T('Vakten er lagret.'));
    } catch (e) {
      if (refused(e)) {
        forgetOp(o.op_id);
        toast(refusedText(e));
      } else toast(T('Fikk fortsatt ikke svar. Prøv igjen om litt.'));
    } finally {
      setBusy(null);
      void invalidateShifts(qc);
      void qc.invalidateQueries({ queryKey: ['create-ops'] });
    }
  };
  const what = (o: CreateOp) => {
    const s = oslo(o.body.start_time);
    const e = oslo(o.body.end_time);
    const u = users.find((x) => x.id === o.body.user_id);
    return `${u ? fullName(u) : T('Ledig vakt')} · ${s?.date ?? ''} ${s?.time ?? ''}–${e?.time ?? ''}`;
  };
  return (
    <output className="al warn" data-testid="pending-creates">
      <span>
        <Icon name="clock" />
      </span>
      <div style={{ display: 'grid', gap: 6, flex: 1, minWidth: 0 }}>
        <b>{T('Ny vakt uten svar fra serveren')}</b>
        {ops.size > 0 && (
          <span>
            {T(
              'Vi vet ikke om disse ble lagret. «Sjekk» sender samme lagring på nytt og lager aldri en vakt til.',
            )}
          </span>
        )}
        {server.isError && (
          <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span data-testid="pending-creates-unread">
              {T('Fikk ikke hentet serverens liste, så det kan finnes flere.')}
            </span>
            <Button small disabled={server.isFetching} onClick={() => void server.refetch()}>
              {T('Prøv igjen')}
            </Button>
          </span>
        )}
        {[...ops.values()].map((o) => (
          <span key={o.op_id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span>{what(o)}</span>
            <Button
              small
              disabled={busy !== null}
              onClick={() => void again(o)}
              data-testid="pending-create-check"
            >
              {T('Sjekk')}
            </Button>
          </span>
        ))}
      </div>
    </output>
  );
}
